import { useMemo, useRef } from 'react';

import { cloneDeep, isEqual } from 'lodash';
import { PlusIcon } from 'lucide-react';
import { Button } from 'twyne-new.ui';

import { parseColor } from '@/components/builder/utils';
import { useColors, useUsedColors } from '@/components/builder/utils/tailwindConfig/hooks';
import { TailwindSandboxPure } from '@/modules/cms/views/post/components/builder/Content/TailwindSandbox';

import { ListContainer, SeparatorSandbox, TitleSandbox } from '../../components/tailwindSandbox';
import { useGetColorFromDocument, GRAY_COLORS, PREDEFINED_COLORS } from '../helpers';

import AddPopover from './AddPopover';
import ContextMenu from './ContextMenu';
import Tooltip from './Tooltip';
import { PaletteProvider, usePalette } from './context';
import { ClearUsedColorsButton, useAddUsedColor } from './usedColors';

export default function PaletteColorsWrapper(props) {
  return <PaletteProvider {...props}>
    <PaletteColors {...props} />
  </PaletteProvider>
}

function PaletteColors({ className = '', name }) {
  useAddUsedColor({ name })
  const { onCloseContextMenu } = usePalette()
  const { value: customColors } = useColors()
  const { value: usedColors } = useUsedColors()

  return <>
    <div className={`relative ${className}`}>
      <ContextMenu />
      <Tooltip />
      <AddColorButton />
      <ClearUsedColorsButton />
      <div className='h-[400px]' onClick={() => onCloseContextMenu()}>
        <TailwindSandboxPure>
          <TitleSandbox>Predefined</TitleSandbox>
          <ColorWrapper colors={PREDEFINED_COLORS} className='h-32' />
          <SeparatorSandbox />

          <TitleSandbox>Gray</TitleSandbox>
          <ColorWrapper colors={GRAY_COLORS} className='h-12' />
          <SeparatorSandbox />

          <TitleSandbox>Custom</TitleSandbox>
          <ColorWrapper colors={customColors} className='h-12' custom key={JSON.stringify(customColors)} />
          <SeparatorSandbox />

          <TitleSandbox>On this page</TitleSandbox>
          <ColorWrapper colors={usedColors} className='h-12' used />
        </TailwindSandboxPure>
      </div>

    </div>
  </>
}

const ColorWrapper = ({ className = '', colors, custom, used }) => {
  const { onCloseContextMenu, onChangeTooltipOpen } = usePalette()

  return <>
    <ListContainer className={className} onCloseContextMenu={onCloseContextMenu}>
      <div className={`grid grid-cols-11 gap-y-1 p-1.5 w-[250px]`} onMouseLeave={() => onChangeTooltipOpen(false)}>
        {colors.map((color, index) => <ColorItem
          key={index}
          value={color}
          custom={custom}
          used={used}
          index={index}
        />)}
      </div>
    </ListContainer>
  </>
}

const ColorItem = ({ value, custom, used, index }) => {
  const { onChangeSelectedColor, selectedColor, onChangePalette, palette: { hover } } = usePalette()

  const colorRef = useRef()
  const color = useGetColorFromDocument({ colorRef, value })

  const selectedClass = useMemo(() => color && isEqual(selectedColor, color?.name || color) ? 'outline-2 outline-blue-400' : '', [selectedColor, color])
  const background = useMemo(() => parseColor(value, 'bg'), [value])

  const handleRightClick = event => {
    event.preventDefault()
    const mouse = { x: event.clientX, y: event.clientY }
    onChangePalette({ rightClick: { color, mouse, custom, used, index } })
  }

  const handleMouseEnter = event => {
    const mouse = { x: event.clientX, y: event.clientY }
    if (isEqual(hover.mouse, mouse)) return
    onChangePalette({ hover: { color, mouse } })
  }

  if (!background) {
    return null
  }

  return <>
    <div
      onClick={() => onChangeSelectedColor(color)}
      onContextMenu={handleRightClick}
      onMouseEnter={handleMouseEnter}
      className='w-4 h-4 cursor-pointer z-10'
    >
      <div className='w-full h-full rounded-xs bg-white'>
        <div className={`${selectedClass} ${background} shadow-xs shadow-gray-400 w-full h-full rounded-xs`} ref={colorRef}>
        </div>
      </div>
    </div>
  </>
}

const AddColorButton = () => {
  const { value: customColors, setValue: setCustomColors } = useColors()

  const handleAdd = newColor => {
    const colorsCopy = cloneDeep(customColors)
    setCustomColors([...colorsCopy, newColor])
  }

  return <AddPopover
    trigger={<Button className='w-fit absolute left-12 py-px bottom-[35%]' variant='light' size='sm'> <PlusIcon size='14' /> </Button>}
    onSubmit={handleAdd}
  />
}

export {
  ColorWrapper,
  ColorItem,
}
